import {
  getEntryPointConfig,
  InternalXingletLoader,
  RouteProvider,
  useRuntime,
} from '@xing-com/crate-xinglet/internal';

import type { Route } from './extract-routes';
import { extractRoutes } from './extract-routes';
import { Redirect, Router } from './reach-router';

const RouteWrapper: React.FC<{
  path: string;
  route: Route;
  Component?: React.ComponentType<React.PropsWithChildren<{ route: Route }>>;
  fallback?: JSX.Element;
}> = ({ route, Component = ({ children }) => children, fallback }) => {
  let children: JSX.Element = <></>;

  for (const subRoute of [...route.parents, route].toReversed()) {
    children = (
      <RouteProvider value={{ children, route: subRoute }}>
        <InternalXingletLoader
          name={subRoute.xinglet}
          route={subRoute}
          fallback={fallback}
        />
      </RouteProvider>
    );
  }

  return <Component route={route}>{children}</Component>;
};

const NotFoundWrapper: React.FC<{
  default: true;
  Component?: React.ComponentType;
}> = ({ Component = () => null }) => {
  return <Component />;
};

export type XingletRouterProps = {
  Route?: React.ComponentType<React.PropsWithChildren<{ route: Route }>>;
  NotFound?: React.ComponentType;
  fallback?: JSX.Element;
};

export const XingletRouter: React.FC<XingletRouterProps> = ({
  Route,
  NotFound,
  fallback,
}) => {
  const { config, metadataMap } = useRuntime();
  const { site } = getEntryPointConfig(metadataMap, config.entryPoint);
  const routes = extractRoutes(metadataMap, site);

  return (
    <Router>
      {routes.map((route) => {
        const path = route.path.replace(/\/\*[\w-]+$/, '/*');

        if (route.redirectTo) {
          return <Redirect key="static" from={path} to={route.redirectTo} />;
        }

        return (
          <RouteWrapper
            key="static"
            path={path}
            route={route}
            fallback={fallback}
            Component={Route}
          />
        );
      })}
      <NotFoundWrapper default Component={NotFound} />
    </Router>
  );
};
